'use client';

import { format, parseISO } from 'date-fns';
import { tz } from '@date-fns/tz';
import type { ShippingPolicyItem } from '../../model/siteconfig/siteconfig';

/**
 * Which data source backed the SLA block:
 * - `measured`: Tier 1 — `dynamicSla.p70Date` / `p90Date` from recent delivery samples.
 * - `fallback`: Tier 2 — `deliveryDateRange.minDate` / `maxDate` derived from the
 *   static shipping duration. Same UI, different source.
 */
export const SHIPPING_SLA_TIER = {
  Measured: 'measured',
  Fallback: 'fallback',
} as const;

export type ShippingSlaTier = (typeof SHIPPING_SLA_TIER)[keyof typeof SHIPPING_SLA_TIER];

export interface FormattedShippingSLA {
  /** Data source used to derive the dates */
  tier: ShippingSlaTier;
  /** Formatted P70 (or fallback min) date, e.g. "Wed, Apr 24" */
  averageDateText: string;
  /** Formatted P90 (or fallback max) date, e.g. "Sun, Apr 28" */
  latestDateText: string;
  /** Seasonal notice line (policy.systemMessage) — undefined when not set */
  seasonalNoticeText?: string;
}

const SLA_DATE_FORMAT = 'EEE, MMM d';

/**
 * Parses a backend SLA date (`YYYY-MM-DD`) as UTC midnight. Without the
 * explicit `Z` suffix, `parseISO` treats a bare date as local time, which
 * shifts the calendar day when the runtime timezone differs from the
 * display timezone. The SLA value is a calendar date, not a timestamp —
 * we want the literal day back.
 */
const parseSlaDate = (isoDate: string) => parseISO(`${isoDate}T00:00:00Z`);

const formatSlaDate = (isoDate: string) => format(parseSlaDate(isoDate), SLA_DATE_FORMAT, { in: tz('UTC') });

/**
 * Derives the Dynamic SLA block (SSD-3986 Phase 2) from a `/shippingPolicies`
 * entry. Returns `undefined` when neither a dynamic SLA nor a delivery date
 * range is available — in that case consumers should render the legacy copy.
 *
 * Tier 1 (`isDynamic` + `dynamicSla`) → uses `p70Date` / `p90Date`.
 * Tier 2 (everything else, while `deliveryDateRange` is present) → uses
 * `deliveryDateRange.minDate` / `maxDate`. Both render identically.
 */
export const formatShippingSLA = (policy: ShippingPolicyItem | undefined): FormattedShippingSLA | undefined => {
  if (!policy) {
    return undefined;
  }

  const seasonalNoticeText = policy.systemMessage?.trim() ? policy.systemMessage.trim() : undefined;

  if (policy.isDynamic && policy.dynamicSla?.p70Date && policy.dynamicSla?.p90Date) {
    return {
      tier: SHIPPING_SLA_TIER.Measured,
      averageDateText: formatSlaDate(policy.dynamicSla.p70Date),
      latestDateText: formatSlaDate(policy.dynamicSla.p90Date),
      seasonalNoticeText,
    };
  }

  if (policy.deliveryDateRange) {
    return {
      tier: SHIPPING_SLA_TIER.Fallback,
      averageDateText: formatSlaDate(policy.deliveryDateRange.minDate),
      latestDateText: formatSlaDate(policy.deliveryDateRange.maxDate),
      seasonalNoticeText,
    };
  }

  return undefined;
};
